import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import Input from "../../components/form/input";
import Btn from "../../components/form/btn";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function CompleteProfileBirthday() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user, updateUser } = useAuth();
  
  const [birthday, setBirthday] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    updatePageTitle(t("auth.completeBirthday"), t("auth.enterYourBirthday"));
    
    console.log("🎂 Birthday page loaded, user:", user);
    
    if (!user) {
      console.log("🎂 No user, redirecting to signin");
      navigate("/signin");
      return;
    }

    // If user doesn't have required previous steps, redirect appropriately
    if (!user.accountType || user.accountType.trim() === '') {
      navigate("/complete-profile");
      return;
    }
    if (!user.phone || user.phone.trim() === '') {
      navigate("/complete-profile/phone");
      return;
    }
    if (!user.gender || user.gender.trim() === '') {
      navigate("/complete-profile/gender");
      return;
    }

    // Only players should access this page
    if (user.accountType !== "player") {
      navigate("/complete-profile/documents");
      return;
    }

    // If user already has birthday or has completed this step, redirect to next step
    if ((user.metaData?.birthday && user.metaData.birthday.trim() !== '') || user.metaData?.hasCompletedBirthday) {
      console.log("🎂 Birthday already completed, redirecting to documents");
      navigate("/complete-profile/documents");
      return;
    }
    
    console.log("🎂 Showing birthday page");

    // Pre-fill existing birthday if available
    setBirthday(user.metaData?.birthday || "");
  }, [user, navigate, updatePageTitle, t]);

  const handleBirthdayChange = (e) => {
    const value = e.target.value;
    setBirthday(value);
    if (errors.birthday) {
      setErrors(prev => ({ ...prev, birthday: "" }));
    }
  };

  const handleBirthdaySubmit = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem("token");

      // Always call the API to update birthday (even if empty)
      const response = await fetch(`${API_URL}/user/set-birthday`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ birthday: birthday.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t("auth.failedToUpdateBirthday"));
      }

      const responseData = await response.json();

      // Update user data
      const updatedUser = { 
        ...user, 
        metaData: { 
          ...user.metaData, 
          birthday: responseData.data?.birthday || null,
          hasCompletedBirthday: true
        } 
      };
      
      // Update both context and localStorage
      updateUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Navigate to documents step immediately
      console.log("🎂 Birthday submitted successfully, navigating to documents...");
      
      // Use setTimeout to ensure state updates are processed
      setTimeout(() => {
        navigate("/complete-profile/documents");
      }, 100);

    } catch (error) {
      console.error("Birthday submission error:", error);
      setErrors({ birthday: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">{t("auth.accountType")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">{t("auth.phone")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2 opacity-75">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">✓</div>
              <span className="text-gray-600 font-medium">{t("auth.gender")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center text-white text-sm font-bold">4</div>
              <span className="text-gray-600 font-medium">{t("auth.birthday")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">5</div>
              <span className="text-gray-400 font-medium">{t("auth.documents")}</span>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#F15213] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="w-10 h-10 text-white">
                <path fill="currentColor" d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z"/>
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#F15213] mb-4">
              {t("auth.yourBirthday")}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("auth.helpUsCelebrate")}
            </p>
          </div>

          {errors.birthday && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.birthday}</p>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Input
                type="date"
                placeholder={t("auth.selectYourBirthday")}
                label={t("auth.birthday")}
                id="birthday"
                value={birthday}
                onChange={handleBirthdayChange}
                error={errors.birthday}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex flex-col items-center">
            <Btn
              onClick={handleBirthdaySubmit}
              isLoading={isLoading}
              className="bg-[#F15213] hover:bg-[#F15213]/90 text-white font-bold py-4 px-12 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              {t("common.continue")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
} 